import React, { FC, useEffect } from 'react';

import { NativeResources } from '@ariksa/inventory-core';
import { Box, Flex, HStack, Stack, UseDisclosureProps } from '@chakra-ui/react';
import { toArray } from 'lodash';
import uniqBy from 'lodash/uniqBy';

import { getIcon } from 'components/DataDisplay';
import { ArrowIconButton, PrimaryButton } from 'components/DataEntry';
import { DataPolicyConditionBlock } from 'containers/Jit/MaskingPolicies/Components/DataPolicyWizard/Components/Criteria/Conditions/DataPolicyConditionBlock';
import { addConditionButtonStyles } from 'containers/Jit/MaskingPolicies/Components/DataPolicyWizard/styles';

const getRoleOptionsForDataGroup = (
  dataGroup,
  allRoleOptions = [],
  dataGroupRoles = {},
  selectedRoles = [],
) => {
  const roleResponse = dataGroupRoles?.[dataGroup?.value];
  const validSelectedRoles = selectedRoles.filter(role => role?.value);
  if (!roleResponse) return validSelectedRoles;

  const roleOptions = (roleResponse.data ?? [])
    .map(role => {
      const roleName = role?.name ?? role?.role_name;
      const roleOption = allRoleOptions.find(
        option => option?.label === roleName || option?.value === roleName,
      );

      return (
        roleOption ?? {
          label: roleName,
          value: roleName,
          icon: getIcon(role?.identity_type ?? NativeResources.SnowflakeRoles),
          data: role,
        }
      );
    })
    .filter(role => role?.value);

  return uniqBy(
    [...roleOptions, ...validSelectedRoles],
    option => option?.value,
  );
};

interface Props {
  state: Record<string, any>;
  updateConditions: (
    item: Record<string, any>,
    key: string,
    value: any,
  ) => void;
  onClickAddCondition: (
    principalType: Record<string, any>,
    maskingOptions?: Record<string, any>,
    policyTypeOptions?: Record<string, any>,
    dataGroup?: Record<string, any>,
    roleOptions?: Record<string, any>[],
    selectDefaultRoleInEdit?: boolean,
  ) => void;
  onDeleteCondition: (item: Record<string, any>, key: string) => void;
  resourceOptions: Record<string, any>;
  dataGroupRoles: Record<string, any>;
  identitiesUsingRoleDisclosure: UseDisclosureProps;
  updateState: (state: any) => void;
}

export const DataPolicySnowflakeConditions: FC<Props> = props => {
  const {
    state,
    onClickAddCondition,
    resourceOptions,
    dataGroupRoles,
    updateConditions,
    updateState,
  } = props;
  const conditions = toArray(state.conditions);
  const dataGroups = state.dataGroups?.length
    ? state.dataGroups
    : state.dataGroupOptions?.slice(0, 1);
  const activeDataGroupIndex = Math.min(
    state?.activeDataGroupIndex ?? 0,
    Math.max((dataGroups?.length ?? 1) - 1, 0),
  );

  const activeDataGroup = dataGroups?.[activeDataGroupIndex] ?? dataGroups?.[0];

  const getConditionDataGroup = condition => toArray(condition?.dataGroups)[0];

  const handleAddDataGroup = () => {
    const dataGroup = state.dataGroupOptions?.find(
      option =>
        !dataGroups?.some(selected => selected?.value === option?.value),
    );
    if (!dataGroup) return;

    props.updateState(prev => {
      const currentDataGroups = prev.dataGroups?.length
        ? prev.dataGroups
        : prev.dataGroupOptions?.slice(0, 1) ?? [];
      const nextDataGroups = [...currentDataGroups, dataGroup];

      return {
        ...prev,
        dataGroups: nextDataGroups,
        activeDataGroupIndex: nextDataGroups.length - 1,
        isInvalid: false,
      };
    });
    onClickAddCondition(
      state.principalType,
      undefined,
      undefined,
      dataGroup,
      getRoleOptionsForDataGroup(
        dataGroup,
        resourceOptions?.[NativeResources.SnowflakeRoles],
        dataGroupRoles,
      ),
      true,
    );
  };

  const handleDataGroupChange = (currentDataGroup, dataGroup) => {
    props.updateState(prev => {
      const nextConditions = Object.entries(prev.conditions ?? {}).reduce(
        (nextConditions, [conditionId, condition]) => {
          const conditionDataGroup = getConditionDataGroup(condition);
          if (conditionDataGroup?.value !== currentDataGroup?.value) {
            nextConditions[conditionId] = condition;
            return nextConditions;
          }

          const dataGroupId =
            Object.keys(condition?.dataGroups ?? {})[0] ??
            `data-group-${conditionId}`;
          nextConditions[conditionId] = {
            ...condition,
            dataGroups: { [dataGroupId]: dataGroup },
          };
          return nextConditions;
        },
        {},
      );
      const selectedDataGroups = prev.dataGroups?.length
        ? prev.dataGroups
        : prev.dataGroupOptions?.slice(0, 1) ?? [];

      return {
        ...prev,
        conditions: nextConditions,
        dataGroups: selectedDataGroups.map(selected =>
          selected?.value === currentDataGroup?.value ? dataGroup : selected,
        ),
        isInvalid: false,
      };
    });
  };

  const handleDeleteDataGroup = dataGroup => {
    const deletedDataGroupIndex = dataGroups?.findIndex(
      selectedDataGroup => selectedDataGroup?.value === dataGroup?.value,
    );

    props.updateState(prev => {
      const nextDataGroups = (prev.dataGroups ?? []).filter(
        selectedDataGroup => selectedDataGroup?.value !== dataGroup?.value,
      );
      const nextActiveDataGroupIndex =
        deletedDataGroupIndex < activeDataGroupIndex
          ? activeDataGroupIndex - 1
          : Math.min(
              activeDataGroupIndex,
              Math.max(nextDataGroups.length - 1, 0),
            );
      const nextConditions = Object.entries(prev.conditions ?? {}).reduce(
        (remainingConditions, [conditionId, condition]) => {
          if (getConditionDataGroup(condition)?.value !== dataGroup?.value) {
            remainingConditions[conditionId] = condition;
          }
          return remainingConditions;
        },
        {},
      );

      return {
        ...prev,
        dataGroups: nextDataGroups,
        activeDataGroupIndex: nextActiveDataGroupIndex,
        conditions: nextConditions,
        isInvalid: false,
      };
    });
  };

  useEffect(() => {
    const firstCondition = toArray(state.conditions)[0];

    if (!firstCondition) {
      if (state?.principalType?.value) {
        onClickAddCondition(state.principalType);
      }
      return;
    }

    const roleUpdates = toArray(state.conditions).reduce(
      (updates, condition) => {
        if (condition?.role?.value) return updates;
        if (state?.isEdit && !condition?.selectDefaultRoleInEdit) {
          return updates;
        }

        const roleOptions = getRoleOptionsForDataGroup(
          getConditionDataGroup(condition),
          resourceOptions?.[NativeResources.SnowflakeRoles],
          dataGroupRoles,
        );
        if (roleOptions[0]) updates[condition.id] = roleOptions[0];
        return updates;
      },
      {},
    );

    if (!Object.keys(roleUpdates).length) return;

    updateState(prev => ({
      ...prev,
      conditions: Object.entries(prev.conditions ?? {}).reduce(
        (nextConditions, [conditionId, condition]) => {
          const selectedRole = roleUpdates[conditionId];
          const sharedMask = selectedRole?.value
            ? Object.values(prev.conditions ?? {}).find(
                existingCondition =>
                  existingCondition?.id !== condition?.id &&
                  existingCondition?.role?.value === selectedRole.value &&
                  existingCondition?.mask,
              )?.mask
            : undefined;

          nextConditions[conditionId] = selectedRole
            ? {
                ...condition,
                role: selectedRole,
                ...(sharedMask ? { mask: sharedMask } : {}),
              }
            : condition;
          return nextConditions;
        },
        {},
      ),
    }));
  }, [
    resourceOptions,
    state.resourceOptions,
    state.conditions,
    state.dataGroups,
    state.principalType,
    state.isEdit,
    dataGroupRoles,
    onClickAddCondition,
    updateState,
  ]);

  const activeBlockConditions = conditions.filter(condition => {
    const conditionDataGroup = getConditionDataGroup(condition);
    return (
      conditionDataGroup?.value === activeDataGroup?.value ||
      (!conditionDataGroup && activeDataGroupIndex === 0)
    );
  });

  return (
    <Stack spacing={3}>
      <Flex justify="flex-end">
        <PrimaryButton
          {...addConditionButtonStyles}
          onClick={handleAddDataGroup}
          isDisabled={
            !state.dataGroupOptions?.some(
              option =>
                !dataGroups?.some(
                  selected => selected?.value === option?.value,
                ),
            )
          }
        >
          + ADD DATA GROUP
        </PrimaryButton>
      </Flex>
      {activeDataGroup && (
        <HStack align="stretch" spacing={4}>
          <Box flex={1} minW={0}>
            <DataPolicyConditionBlock
              key={activeDataGroup?.value ?? activeDataGroupIndex}
              state={state}
              conditions={activeBlockConditions}
              dataGroupOptions={state.dataGroupOptions?.filter(
                option =>
                  option?.value === activeDataGroup?.value ||
                  !dataGroups?.some(
                    selected => selected?.value === option?.value,
                  ),
              )}
              roleOptions={getRoleOptionsForDataGroup(
                activeDataGroup,
                resourceOptions?.[NativeResources.SnowflakeRoles],
                dataGroupRoles,
                activeBlockConditions.map(condition => condition?.role),
              )}
              selectedDataGroup={activeDataGroup}
              updateConditions={updateConditions}
              onClickAddCondition={onClickAddCondition}
              onDeleteCondition={props.onDeleteCondition}
              onDeleteDataGroup={handleDeleteDataGroup}
              onChangeDataGroup={value =>
                handleDataGroupChange(activeDataGroup, value)
              }
              isDeleteDisabled={dataGroups?.length <= 1}
            />
          </Box>
          <Stack justify="flex-start" spacing={4}>
            <ArrowIconButton
              direction="up"
              aria-label="up"
              isDisabled={activeDataGroupIndex === 0}
              onClick={() =>
                updateState({
                  activeDataGroupIndex: activeDataGroupIndex - 1,
                })
              }
            />
            <ArrowIconButton
              direction="down"
              aria-label="down"
              isDisabled={activeDataGroupIndex >= (dataGroups?.length ?? 1) - 1}
              onClick={() =>
                updateState({
                  activeDataGroupIndex: activeDataGroupIndex + 1,
                })
              }
            />
          </Stack>
        </HStack>
      )}
    </Stack>
  );
};
